// Force a refresh when an updated Service Worker takes control,
// avoiding stale UI after deployments. Also proactively clears
// stale Cache Storage entries so module chunks are re-fetched.
//
// Strategy (belt & suspenders — evita "Em breve" fantasma por cache velho):
// 1. Ao carregar: registration.update() imediato e limpeza de caches órfãos.
// 2. Polling periódico (a cada 60s) para detectar novas versões.
// 3. Update ao voltar o foco / aba visível (usuário voltando ao app).
// 4. Update ao reconectar à internet.
// 5. Assim que um novo SW instala, ativa (SKIP_WAITING) e recarrega a página.
export function setupSwAutoUpdate(onUpdate?: () => void) {
  if (!('serviceWorker' in navigator)) return;

  let reloading = false;

  const clearAllCaches = async () => {
    if (!('caches' in window)) return;
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    } catch {
      // noop
    }
  };

  const doReload = async () => {
    if (reloading) return;
    reloading = true;
    await clearAllCaches();
    onUpdate?.();
    // Fallback caso onUpdate não recarregue por algum motivo.
    setTimeout(() => {
      try { window.location.reload(); } catch { /* noop */ }
    }, 250);
  };

  // Sempre que um novo SW tomar controle, recarregar (garante UI fresca).
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    void doReload();
  });

  void navigator.serviceWorker.ready
    .then((registration) => {
      const activateWaiting = () => {
        if (registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
      };

      const checkForUpdate = () => {
        registration.update().then(activateWaiting).catch(() => undefined);
      };

      // Check inicial + ativar qualquer waiting worker acumulado.
      checkForUpdate();

      registration.addEventListener('updatefound', () => {
        const installing = registration.installing;
        if (!installing) return;
        installing.addEventListener('statechange', () => {
          if (installing.state === 'installed') {
            // Novo SW pronto → ativa imediatamente.
            installing.postMessage({ type: 'SKIP_WAITING' });
          }
        });
      });

      // Polling periódico.
      const interval = window.setInterval(checkForUpdate, 60_000);

      // Atualiza ao voltar o foco / aba visível / reconectar.
      const onVisible = () => {
        if (document.visibilityState === 'visible') checkForUpdate();
      };
      document.addEventListener('visibilitychange', onVisible);
      window.addEventListener('focus', checkForUpdate);
      window.addEventListener('online', checkForUpdate);

      // Cleanup defensivo (SPA raramente descarrega, mas evita leak).
      window.addEventListener('beforeunload', () => {
        window.clearInterval(interval);
      });
    })
    .catch(() => undefined);
}
